import CustomInput from '../../components/CustomInput';
import { addToast, Input, Select, SelectItem, Textarea } from '@heroui/react';

import { useFormContext } from 'react-hook-form';
import CustomButton from '../../components/CustomButton';
import { Address, PersonalGet } from '../../../utils/type';

import {
  useRemoveUserAddress,
  useAddUserAddress,
  useUpdateUserAddress,
} from '../../../hook/useMutationData';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { LuLoaderCircle } from 'react-icons/lu';
import { useAuth } from '../../../context/AuthContext';
import {
  ACCOUNT_STATUS,
  canadaProvincesAndTerritories,
} from '../../../utils/enum';
import { useTranslation } from 'react-i18next';

const AddressSection = ({ data }: any) => {
  const { t } = useTranslation();
  const { userId, customerAccountStatus } = useAuth();
  const [address, setAddress] = useState<any>(null);
  const [addId, setAddId] = useState<string>('');
  const [error, setError] = useState<{ error?: string; id?: string } | null>(
    null
  );
  const {
    register,
    watch,
    trigger,
    setValue,
    formState: { errors },
  } = useFormContext<PersonalGet>();

  const { mutateAsync, isPending: isAddressAddLoading } = useAddUserAddress();
  const { mutateAsync: removeAddress, isPending: isAddressRemoveLoading } =
    useRemoveUserAddress();
  const { mutate: updateAddress, isPending: isAddressUpdateLoading } =
    useUpdateUserAddress();

  // Track the street of the address being added so we can reliably find it
  // when the refetched list arrives (regardless of API ordering).
  const pendingNewStreetRef = useRef<string | null>(null);

  // ---- address autocomplete ---- //
  const myAPIKey = import.meta.env.VITE_GEOLOCATION_API_KEY;
  const myCountryCode = import.meta.env.VITE_COUNTRY_CODE;
  const myCountry = import.meta.env.VITE_COUNTRY;
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const addressContainerRef = useRef<HTMLDivElement | null>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        addressContainerRef.current &&
        !addressContainerRef.current.contains(event.target as Node)
      ) {
        setAddressSuggestions([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced Geoapify autocomplete
  useEffect(() => {
    if (addressQuery.length < 2) {
      setAddressSuggestions([]);
      return;
    }
    const fetchSuggestions = async () => {
      try {
        const res = await fetch(
          `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
            addressQuery
          )}&limit=10&filter=countrycode:${myCountryCode}&apiKey=${myAPIKey}`
        );
        const data = await res.json();
        setAddressSuggestions(data.features || []);
      } catch {
        setAddressSuggestions([]);
      }
    };
    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [addressQuery, myAPIKey, myCountryCode]);

  const selectAddressSuggestion = useCallback(
    (suggestion: any) => {
      const selected = suggestion?.properties;
      if (!selected) return;
      setAddressQuery(selected.formatted || '');
      setValue('address.street', selected.address_line1 || '', {
        shouldValidate: true,
      });
      setValue('address.city', selected.city || '', { shouldValidate: true });
      setValue('address.state', selected.state || '', { shouldValidate: true });
      setValue('address.postalCode', selected.postcode || '', {
        shouldValidate: true,
      });
      setAddressSuggestions([]);
    },
    [setValue]
  );

  // Keep local address list in sync with user data
  const prevLengthRef = useRef<number>(0);
  const isInitialLoadRef = useRef<boolean>(true);

  useEffect(() => {
    const addresses = data?.user?.address;
    setAddress(addresses || null);

    const len = Array.isArray(addresses) ? addresses.length : 0;

    // Auto-select the newly added address when the list grows.
    // Skip only the very first time data arrives (initial load).
    if (len > 0 && len > prevLengthRef.current && !isInitialLoadRef.current) {
      const newAddr = pendingNewStreetRef.current
        ? addresses.find(
            (a: any) => a?.addressLine1 === pendingNewStreetRef.current
          )
        : null;
      const selected = newAddr || addresses[len - 1];
      if (selected) {
        // Defer to next tick to ensure any parent form reset triggered
        // by the same data update completes before we set our values.
        setTimeout(() => handleSelectAddress(selected), 0);
      }
      pendingNewStreetRef.current = null;
    }
    prevLengthRef.current = len;
    isInitialLoadRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // UPDATE ADDRESS
  const handleAddAddress = async () => {
    try {
      setError({ error: '', id: '' });
      const isValid = await trigger([
        'address.street',
        'address.city',
        'address.state',
        'address.postalCode',
      ]);
      // if (errors?.address && Object.keys(errors).length > 0) {
      if (!isValid) {
        addToast({
          title: t('common.validation_error'),
          description: t('booking.address_validation_error'),
          radius: 'md',
          color: 'danger',
        });
        return;
      }

      const address = watch('address');

      if (!userId) {
        throw new Error('User ID is not available');
      }
      if (!address) {
        throw new Error('Please select an saved address');
      }

      const payload = {
        address: [
          {
            addressLine1: address.street,
            country: 'Canada',
            state: address.state,
            city: address.city,
            postalCode: address.postalCode,
          },
        ],
      };
      console.log('final address ', payload);

      // Remember the street being saved so the useEffect fallback can
      // reliably find this address in the refetched list.
      pendingNewStreetRef.current = address.street || null;

      const res: any = await mutateAsync({ id: userId, data: payload });

      // Try to auto-select from the mutation response
      const addresses = res?.user?.address || res?.data?.user?.address;
      const matched =
        Array.isArray(addresses) &&
        addresses.find(
          (a: any) => a?.addressLine1 === pendingNewStreetRef.current
        );
      const newAddress =
        matched ||
        (Array.isArray(addresses)
          ? addresses[addresses.length - 1]
          : undefined);
      if (newAddress) {
        handleSelectAddress(newAddress);
        pendingNewStreetRef.current = null;
      }

      addToast({
        title: t('common.success'),
        description: t('booking.address_add_success'),
        radius: 'md',
        color: 'success',
      });
    } catch (error: any) {
      addToast({
        title: 'Error Updating User Address',
        description: error.message,
        radius: 'md',
        color: 'danger',
      });
      console.error('Error update user address: ', error);
    }
  };

  const handleUpdateAddress = async () => {
    try {
      setError({ error: '', id: '' });
      // trigger();
      const isValid = await trigger([
        'address.street',
        'address.city',
        'address.state',
        'address.postalCode',
      ]);
      // if (errors?.address && Object.keys(errors).length > 0) {
      if (!isValid) {
        addToast({
          title: t('common.validation_error'),
          description: t('booking.address_validation_error'),
          radius: 'md',
          color: 'danger',
        });
        return;
      }

      const address = watch('address');

      if (!userId) {
        throw new Error('User ID is not available');
      }
      if (!address) {
        throw new Error('Address is not available');
      }

      if (!address.addressId) {
        throw new Error('Address ID is not found');
      }

      const payload = {
        address: [
          {
            addressLine1: address.street,
            country: 'Canada',
            state: address.state,
            city: address.city,
            postalCode: address.postalCode,
          },
        ],
      };

      console.log('final address ', payload);

      await updateAddress({
        id: userId,
        data: payload,
        addressId: address.addressId,
      });

      addToast({
        title: t('common.success'),
        description: t('booking.address_update_success'),
        radius: 'md',
        color: 'success',
      });
    } catch (error: any) {
      addToast({
        title: 'Error Updating User Address',
        description: error.message,
        radius: 'md',
        color: 'danger',
      });
      console.error('Error update user address: ', error);
    }
  };

  const handleRemoveAddress = async (address: Address) => {
    try {
      const role = 'provider';
      if (!address?.addressId) {
        throw new Error('Address ID is not found');
      }
      setAddId(address?.addressId);
      if (role === 'provider' && address?.isLegalAddress) {
        setError({
          error: t('booking.legal_address_error'),
          id: address?.addressId,
        });
        setAddId('');
        return;
      }

      if (!userId) {
        addToast({
          title: 'User Id is Missing',
          description: 'User ID is missing. Please try again.',
          radius: 'md',
          color: 'danger',
        });
      }

      const res = await removeAddress({
        id: userId ? userId : '',
        addressId: address?.addressId,
      });

      if (res?.success) {
        setAddId('');
        addToast({
          title: t('common.success'),
          description: t('booking.address_remove_success'),
          radius: 'md',
          color: 'success',
        });
      }
    } catch (error: any) {
      addToast({
        title: 'Error Removing User Address',
        description: error.message,
        radius: 'md',
        color: 'danger',
      });
      console.error('Error remove user address: ', error);
    }
  };

  const handleSelectAddress = (addr: Address) => {
    setError({ error: '', id: '' });
    if (!addr) {
      addToast({
        title: 'Address Not Found',
        description: 'Address not found. Please select a valid address.',
        radius: 'md',
        color: 'danger',
      });
      return;
    }
    setAddId(addr?.addressId || ' ');
    // Set the entire address object at once — avoids the resetField
    // race condition where fields briefly clear before repopulating.
    setValue('address', {
      addressId: addr?.addressId || '',
      street: addr?.addressLine1 || '',
      city: addr?.city || '',
      state: addr?.state || '',
      postalCode: addr?.postalCode || '',
    });
    trigger([
      'address.street',
      'address.city',
      'address.state',
      'address.postalCode',
    ]);
  };

  useEffect(() => {
    setTimeout(() => {
      if (error?.error) {
        setError({
          error: '',
          id: '',
        });
      }
    }, 5000);
  }, [error]);

  console.log('addId: ', addId);

  return (
    <div>
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-3">
        <div className="border-b mb-2 flex justify-between items-center">
          <p className="text-title-bold  mb-2">
            {t('booking.address_information')}
          </p>
        </div>

        {/* CARDS */}
        <div className="grid xl:grid-cols-3 2xl:grid-cols-4  gap-2">
          <div className=" xl:col-span-2 2xl:col-span-3">
            <div className="grid xl:grid-row-2 gap-3 mt-2">
              <div className="grid xl:grid-cols-2 2xl:grid-cols-3 gap-3 mt-1">
                <CustomInput
                  type="text"
                  isRequired
                  isDisabled
                  value={watch('firstName')}
                  label={t('booking.first_name')}
                  {...register('firstName')}
                  isInvalid={!!errors?.firstName}
                  errorMessage={errors?.firstName?.message}
                  className="cursor-not-allowed"
                  autoComplete="off"
                />

                <CustomInput
                  type="text"
                  isRequired
                  isDisabled
                  value={watch('lastName')}
                  label={t('booking.last_name')}
                  {...register('lastName')}
                  isInvalid={!!errors?.lastName}
                  errorMessage={errors?.lastName?.message}
                  className="cursor-not-allowed"
                  autoComplete="off"
                />
                <CustomInput
                  type="text"
                  isRequired
                  value={watch('email')}
                  isDisabled
                  label={t('booking.email')}
                  {...register('email')}
                  placeholder={t('booking.email_placeholder')}
                  isInvalid={!!errors?.email}
                  errorMessage={errors?.email?.message}
                  className="cursor-not-allowed"
                  autoComplete="off"
                />

                <CustomInput
                  type="text"
                  isRequired
                  value={watch('phone')}
                  label={t('booking.contact_no')}
                  {...register('phone', {
                    shouldUnregister: false, // keep value
                  })}
                  placeholder="+1XXXXXXXXXX"
                  isInvalid={!!errors?.phone}
                  errorMessage={errors?.phone?.message}
                  className="bg-white"
                  autoComplete="tel"
                />
              </div>
              <div className="border-b"></div>

              <div ref={addressContainerRef} className="relative">
                <CustomInput
                  type="text"
                  isRequired
                  label={t('booking.street_address')}
                  value={watch('address.street')}
                  {...register('address.street')}
                  placeholder={`Search address only in ${myCountry}`}
                  isInvalid={!!errors?.address?.street}
                  errorMessage={errors?.address?.street?.message}
                  className="bg-white"
                  autoComplete="street-address"
                  onChange={(e) => {
                    setAddressQuery(e.target.value);
                    setValue('address.street', e.target.value, {
                      shouldValidate: true,
                    });
                  }}
                />

                {addressSuggestions.length > 0 && (
                  <div className="absolute z-50 max-w-[1000px]  right-1 left-1 bg-gray-100 border-gray-400 mt-1  rounded-md max-h-90 overflow-auto shadow-lg border-1 ">
                    {addressSuggestions.map((place, idx) => (
                      <div
                        key={idx}
                        onClick={() => selectAddressSuggestion(place)}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-400 dark:hover:bg-sideBarBackground text-sm"
                      >
                        {place?.properties?.formatted}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid xl:grid-cols-2 2xl:grid-cols-3 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600 font-medium">
                    {t('booking.country')}
                  </label>
                  <Input
                    value="Canada"
                    isReadOnly
                    size="sm"
                    variant="bordered"
                    classNames={{
                      input: 'text-xs text-gray-500 cursor-not-allowed',
                      inputWrapper: 'border border-gray-300 bg-gray-50',
                    }}
                  />
                </div>
                <CustomInput
                  type="text"
                  isRequired
                  value={watch('address.city')}
                  label={t('booking.city')}
                  placeholder={t('booking.city_placeholder')}
                  {...register('address.city')}
                  isInvalid={!!errors?.address?.city}
                  errorMessage={errors?.address?.city?.message}
                  className="bg-white"
                  autoComplete="address-level2"
                />
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600 font-medium">
                    {t('booking.state')} <span className="text-red-500">*</span>
                  </label>
                  <Select
                    placeholder={t('booking.state_placeholder')}
                    selectedKeys={
                      watch('address.state') ? [watch('address.state')] : []
                    }
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0] as string;
                      setValue('address.state', selectedKey || '', {
                        shouldValidate: true,
                      });
                    }}
                    size="sm"
                    variant="bordered"
                    isInvalid={!!errors?.address?.state}
                    classNames={{
                      trigger: 'border border-gray-300 text-xs',
                      value: 'text-xs',
                      listbox: 'text-xs',
                    }}
                  >
                    {canadaProvincesAndTerritories.map((province) => (
                      <SelectItem key={province.key}>
                        {province.label}
                      </SelectItem>
                    ))}
                  </Select>
                  {errors?.address?.state && (
                    <p className="text-[11px] text-red-500">
                      {errors?.address?.state?.message}
                    </p>
                  )}
                </div>
                <CustomInput
                  type="text"
                  isRequired
                  value={watch('address.postalCode')}
                  label={t('booking.postal_code')}
                  placeholder={t('booking.postal_code_placeholder')}
                  {...register('address.postalCode')}
                  isInvalid={!!errors?.address?.postalCode}
                  errorMessage={errors?.address?.postalCode?.message}
                  className="bg-white"
                  autoComplete="postal-code"
                />
              </div>

              {customerAccountStatus === ACCOUNT_STATUS.ACTIVE && (
                <div className="flex gap-3 justify-end mt-2">
                  {data?.user?.address?.length > 0 && (
                    <CustomButton
                      label={t('booking.update_current_address')}
                      variant="flat"
                      color="default"
                      type="button"
                      onPress={() => handleUpdateAddress()}
                      isLoading={isAddressUpdateLoading}
                    />
                  )}
                  {data?.user?.address?.length < 4 && (
                    <CustomButton
                      label={t('booking.save_new_address')}
                      variant="solid"
                      color="primary"
                      type="button"
                      onPress={() => handleAddAddress()}
                      isLoading={isAddressAddLoading}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="border-t mt-3"></div>
            <Textarea
              label={t('booking.booking_note')}
              labelPlacement="outside"
              variant="bordered"
              className="mt-2 bg-white"
              placeholder={t('booking.booking_note_placeholder')}
              classNames={{
                input:
                  'placeholder:text-gray-400 placeholder:text-xs text-xs bg-white',
                label: 'text-xs text-gray-700',
                base: 'text-xs',
                description: 'text-[11px]',
                inputWrapper: 'border border-gray-300 ',
              }}
              {...register('bookingNotes', {
                maxLength: {
                  value: 500,
                  message: t('booking.booking_note_maxlength'),
                },
              })}
              isInvalid={!!errors?.bookingNotes}
              errorMessage={errors?.bookingNotes?.message}
            />
          </div>

          <div className="order-first xl:order-none border-l border-gray-200 pl-4 flex flex-col gap-2">
            {/* 1. Fixed Empty State Condition */}
            {(!address || address.length === 0) && (
              <div className="text-center py-3 border border-dashed border-gray-200 rounded-lg">
                <p className="text-xs text-gray-400">
                  {t('booking.no_saved_addresses')}
                </p>
              </div>
            )}

            {address?.map((addr: any, index: number) => {
              const isRemovingThis =
                isAddressRemoveLoading && addId === addr.addressId;
              const hasThisError = error?.error && error.id === addr.addressId;

              return (
                <button
                  key={addr.addressId || index}
                  type="button"
                  onClick={() => handleSelectAddress(addr)}
                  disabled={isAddressRemoveLoading}
                  className={`group flex flex-col items-start w-full border p-2.5 rounded-xl text-left transition-all duration-200
      focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2  
      ${
        hasThisError
          ? 'border-red-300 bg-red-50/30'
          : addId === addr.addressId
            ? 'border-primary bg-primary/5 '
            : 'border-gray-200 hover:border-primary/50 hover:bg-slate-50/50'
      }`}
                >
                  {/* Header Metadata Section */}
                  <div className="flex justify-between w-full items-center mb-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                      {t('booking.address_label', { number: index + 1 })}
                    </span>

                    {/* Contextual Action Items */}
                    <div className="flex items-center justify-center min-w-[24px] min-h-[24px]">
                      {isRemovingThis ? (
                        <LuLoaderCircle
                          className="text-gray-400 animate-spin"
                          size={16}
                        />
                      ) : (
                        <IoClose
                          className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full p-0.5 transition-colors cursor-pointer opacity-80 group-hover:opacity-100"
                          size={20}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveAddress(addr);
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Formatted Address Presentation */}
                  <p className="text-xs font-medium text-gray-700 leading-relaxed flex-1">
                    {[
                      addr?.addressLine1,
                      addr?.city,
                      addr?.state,
                      addr?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>

                  {/* Use This Address Action */}
                  {addId !== addr.addressId && (
                    <div className="mt-1.5 pt-1.5 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => handleSelectAddress(addr)}
                        className="text-xs font-semibold text-secondary hover:text-secondary/80 transition"
                      >
                        {t('booking.use_this_address', 'Use this address')}
                      </button>
                    </div>
                  )}

                  {/* Smooth, Self-Contained Error Alert Layout */}
                  {hasThisError && (
                    <div className="flex items-center gap-1.5 py-1 px-2.5 bg-red-50 border border-red-200 rounded-md mt-1.5 w-full animate-fadeIn">
                      <span className="text-[11px] font-medium text-red-600">
                        {error.error}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressSection;
